import type { FC } from 'react';
import React, { useCallback } from 'react';
import Logo from '../../assets/pn-logo-with-name.svg';
import {
  Box,
  CircularProgress,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Label } from '@pflegenavi/web-components';
import { useFormatting } from '@pflegenavi/frontend/localization';
import type { PaymentInitiatedBy } from '@pflegenavi/shared/api';
import { DirectDebitStatus, ResidentState } from '@pflegenavi/shared/api';
import type { LabelColor } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { BalanceStatusLabel } from '../general/BalanceStatusLabel';

interface ResidentCardProps {
  resident:
    | {
        id: string;
        state: ResidentState;
        firstName: string;
        lastName: string;
        balance: {
          amount: number;
          negativeDueDate: Date | null;
          thresholdDueDate: Date | null;
        };
      }
    | undefined;
  nursingHomeName: string | undefined;
  directDebitStatus: DirectDebitStatus;
  paymentInitiation?: PaymentInitiatedBy;
  isResidentLoading: boolean;
  isDirectDebitStatusLoading: boolean;
}

export const ResidentCard: FC<ResidentCardProps> = ({
  resident,
  nursingHomeName,
  directDebitStatus,
  paymentInitiation,
  isResidentLoading,
  isDirectDebitStatusLoading,
}: ResidentCardProps) => {
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();

  const isInactive = resident?.state === ResidentState.archived;

  const getBadgeColor = useCallback((): LabelColor => {
    if (isInactive) {
      return 'info';
    }
    if (paymentInitiation === 'familyMember') {
      return 'info';
    }
    if (directDebitStatus === DirectDebitStatus.Pending) {
      return 'warning';
    }
    if (directDebitStatus === DirectDebitStatus.Inactive) {
      return 'error';
    }
    return 'success';
  }, [isInactive, paymentInitiation, directDebitStatus]);

  if (
    !resident ||
    !nursingHomeName ||
    isResidentLoading ||
    isDirectDebitStatusLoading
  ) {
    return (
      <CardContainer>
        <CardInner spacing={1}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <img src={Logo} alt="Pflegenavi" height={22} />
          </Stack>
          <Stack
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress color="info" sx={{ mt: 5 }} />
          </Stack>
        </CardInner>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <CardInner justifyContent="space-between" spacing={1}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <img src={Logo} alt="Pflegenavi" height={22} />
        </Stack>

        <Stack
          direction="column"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'grey.400',
              mb: 0,
            }}
          >
            {t('mobile.family-member.current-balance')}
          </Typography>
          <Typography variant="h3" color="white" sx={{ lineHeight: 1.3 }}>
            {fCurrencyCents(resident.balance.amount)}
          </Typography>
          {!isInactive && (
            <BalanceStatusLabel
              negativeBalance={resident.balance.amount < 0}
              lowBalance={Boolean(resident.balance.thresholdDueDate)}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'grey.400',
              mb: 0,
            }}
          >
            {isInactive
              ? t('mobile.family-member.account-status')
              : t('mobile.family-member.direct-debit')}
          </Typography>
          <Label
            color={getBadgeColor()}
            variant="filled"
            sx={{ ml: 0.5, color: 'white' }}
          >
            {isInactive
              ? t('mobile.family-member.settled')
              : paymentInitiation === 'familyMember'
              ? t('direct-debit.status.manual')
              : t(renderDirectDebitStatus(directDebitStatus))}
          </Label>
        </Stack>

        <Stack
          direction="column"
          sx={{
            justifyContent: 'flex-start',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'grey.500',
              mb: 0,
            }}
          >
            {t('common.resident')}
          </Typography>
          <Typography variant="h6" color="white" sx={{ lineHeight: 1.2 }}>
            {`${resident.firstName} ${resident.lastName}`}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'grey.500',
              mb: 0,
            }}
          >
            {nursingHomeName}
          </Typography>
        </Stack>
      </CardInner>
    </CardContainer>
  );
};

export const renderDirectDebitStatus = (
  directDebit: DirectDebitStatus
): string => {
  const directDebitString = directDebit.toLowerCase();
  return `direct-debit.status.${directDebitString}`;
};

const CardContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    minHeight: 250,
    borderRadius: 4,
    height: '100%',
    overflow: 'hidden',
    bgcolor: 'primary.main',
    position: 'relative',
    boxShadow: '-5px 5px 20px 2px rgba(150,170,180,0.5)',
  })
);
const CardInner = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: '100%',
    height: '100%',
    paddingX: 3,
    paddingY: 2,
    overflow: 'hidden',
    zIndex: 2,
    position: 'relative',
  })
);
